'use client';

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui';
import { useAppDispatch, useAppSelector, selectActiveTenantId } from '@/store/hooks';
import { setActiveTenant } from '@/store/slices/tenantSlice';
import { selectUser } from '@/store/slices/authSlice';

export interface TenantSwitcherProps {
  expanded?: boolean;
}

/**
 * Pure UI component for switching between tenants.
 *
 * All data fetching and tenant resolution is handled by ClientTenantSyncProvider.
 * This component reads from Redux and dispatches setActiveTenant on user interaction.
 */
export function TenantSwitcher({ expanded = false }: TenantSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const dispatch = useAppDispatch();

  const activeTenantId = useAppSelector(selectActiveTenantId);
  const activeTenantName = useAppSelector((s) => s.tenant.activeTenantName);
  const tenants = useAppSelector((s) => s.tenant.tenants);
  const user = useAppSelector(selectUser);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleSelectTenant = (tenant: {
    tenantId: string;
    name: string;
    code?: string;
    type?: string;
    organizationId?: string;
    organizationName?: string;
  }) => {
    if (tenant.code) {
      // Navigate first and let AuthenticationController observe that the URL
      // tenant differs from the signed token. It is the only component allowed
      // to start the organization-scoped Auth0 login transaction.
      window.location.assign(`/${encodeURIComponent(tenant.code)}/dashboard`);
      return;
    }

    dispatch(
      setActiveTenant({
        tenant: {
          tenantId: tenant.tenantId,
          name: tenant.name,
          code: tenant.code,
          type: tenant.type,
          organizationId: tenant.organizationId,
          organizationName: tenant.organizationName,
        },
        userSub: user?.sub,
      }),
    );
    setIsOpen(false);
  };

  const displayName = activeTenantName || 'Select tenant';
  const tenantCount = tenants.length;

  const buttonClassName = cn(
    'group flex relative items-center justify-start',
    'h-(--sidebar-link-height) gap-3 px-2.5 translate-x-1',
    expanded ? 'w-full' : 'w-(--sidebar-link-height)',
    'rounded-(--sidebar-link-radius)',
    'transition-all duration-200 ease-in-out',
    expanded && 'hover:bg-(--sidebar-link-bg-hover)',
  );

  const buttonContent = (
    <>
      <span className="relative shrink-0 flex items-center justify-center">
        <i
          className={cn(
            'pi pi-building',
            '[font-size:var(--sidebar-icon-size)]',
            'transition-colors duration-150',
            'text-(--sidebar-link-icon-default) group-hover:text-(--sidebar-link-icon-hover)',
          )}
          aria-hidden="true"
        />
      </span>

      <div
        className={cn(
          'flex flex-col items-start overflow-hidden whitespace-nowrap',
          'transition-all duration-200 ease-in-out',
          expanded ? 'opacity-100 max-w-[200px]' : 'opacity-0 max-w-0',
        )}
        aria-hidden={!expanded}
      >
        <span className="body-sm-medium text-text-primary truncate max-w-[132px]">
          {displayName}
        </span>
        <span className="body-xs-regular text-text-tertiary">
          {tenantCount === 1 ? '1 tenant' : `${tenantCount} tenants`}
        </span>
      </div>

      {expanded && (
        <i
          className={cn(
            'pi pi-chevron-down text-xs text-text-tertiary',
            'absolute right-4 top-1/2 -translate-y-1/2',
            'transition-transform duration-200',
            isOpen ? 'rotate-180' : 'rotate-0',
          )}
          aria-hidden="true"
        />
      )}
    </>
  );

  if (!isMounted) {
    return (
      <button type="button" className={buttonClassName} title="Select tenant">
        {buttonContent}
      </button>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button type="button" className={buttonClassName} title="Select tenant">
          {buttonContent}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="right"
        align="start"
        className="w-[236px] max-h-[300px] overflow-y-auto"
      >
        {tenants.map((tenant) => (
          <DropdownMenuItem
            key={tenant.tenantId}
            onClick={() => handleSelectTenant(tenant)}
            className={cn(
              'gap-2 py-3 cursor-pointer',
              tenant.tenantId === activeTenantId && 'bg-accent-primary/5',
            )}
          >
            <i
              className="pi pi-building text-sm text-text-secondary"
              aria-hidden="true"
            />
            <span className="truncate">{tenant.name}</span>
            {tenant.tenantId === activeTenantId && (
              <i
                className="pi pi-check text-sm text-accent-primary ml-auto"
                aria-hidden="true"
              />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
